import './membership-tier.component.scss';

import type { FunctionComponent } from 'preact';
import { memo } from 'preact/compat';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { useAuth } from '../../../auth/hooks/use-auth.hook';
import { useShopifyCurrency } from '../../../common/currency/shopify-currency.hook';
import { IconCaret } from '../../../common/icons/icon-caret';
import { useTranslation } from '../../../i18n';
import { modalService } from '../../../modal/modal.service';
import type { AccountSettings } from '../../../models/common.model';
import type { AppDispatch } from '../../../store';
import { useAppSelector } from '../../../store/hooks';
import { getCurrentUrl } from '../../../utils/window.utils';
import type { MembershipConfirmCancelPendingActionModalProps } from '../../modals/membership-confirm-cancel-pending-action.modal';
import { MembershipConfirmCancelPendingActionModal } from '../../modals/membership-confirm-cancel-pending-action.modal';
import type { MembershipConfirmChangeModalProps } from '../../modals/membership-confirm-change.modal';
import { MembershipConfirmChangeModal } from '../../modals/membership-confirm-change.modal';
import type { MembershipManageModalProps } from '../../modals/membership-manage.modal';
import { MembershipManageModal } from '../../modals/membership-manage.modal';
import type {
  Membership,
  MembershipContractUpdateRequest,
  Tier,
} from '../../models/membership.model';
import { ContractState, InteractionType, PendingChangeState } from '../../models/membership.model';
import { MembershipJoinFlowService } from '../../services/membership-join-flow.service';
import { selectCreatingContract, selectIsTierAddingToCart } from '../../store/membership.selectors';
import { cancelMembershipUpdate, updateMembership } from '../../store/membership.thunk';
import { formatDate, getMembershipTermName, isNonRenewingTerm } from '../../utils/membership.formatters';
import {
  getChangeType,
  getMembershipLabel,
  isContractFreeTier,
  isTierCurrent,
  isTierSoldOut,
} from '../../utils/membership.utlis';

interface MembershipTierProps {
  membership: Membership;
  tier: Tier;
  accountSettings?: AccountSettings;
  showProgramName?: boolean;
  showTierImage?: boolean;
  showTierName?: boolean;
  showTierDescription?: boolean;
  showPrice?: boolean;
  showQuickAdd?: boolean;
  showViewPerks?: boolean;
  titleFormat?: 'bullet' | 'small';
  /** 'card' is the join-page tier card; 'row' is a compact horizontal layout
   *  for embedding on a host surface (e.g. the need-access modal) — the host
   *  owns text color, and the row collapses back to a card based on the
   *  nearest size container's width. */
  layout?: 'card' | 'row';
  redirectToCurrentUrl?: boolean;
  redirectUrl?: string;
  key?: string;
}

// Custom hook for tier state (extracted for clarity and reusability)
function useTierState(tier: Tier, showTierName: boolean, showProgramName: boolean) {
  const [termId, setTermId] = useState(tier?.terms?.[0]?.id || '');
  const [localShowTierName, setLocalShowTierName] = useState(showTierName);
  const [localShowProgramName, setLocalShowProgramName] = useState(showProgramName);

  useEffect(() => {
    if (tier?.terms && tier.terms.length >= 1) {
      setTermId(tier.terms[0].id);
    }

    if (showTierName && !tier?.title) {
      setLocalShowTierName(false);
      // Only show program name as fallback if it was enabled
      if (showProgramName) {
        setLocalShowProgramName(true);
      }
    }
  }, [tier, showTierName, showProgramName]);

  return {
    termId,
    setTermId,
    localShowTierName,
    localShowProgramName,
  };
}

const MembershipTierComponent: FunctionComponent<MembershipTierProps> = ({
  membership,
  tier,
  accountSettings,
  showProgramName = false,
  showTierImage = true,
  showTierName = true,
  showTierDescription = true,
  showPrice = true,
  showQuickAdd = true,
  showViewPerks = false,
  titleFormat = 'bullet',
  layout = 'card',
  redirectToCurrentUrl = false,
  redirectUrl,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { shopifyCurrency } = useShopifyCurrency();
  const { openAuthModal } = useAuth();
  const { t } = useTranslation();

  // Local state (extracted to custom hook)
  // Destructure for stable references in useCallback dependencies
  const {
    termId,
    setTermId,
    localShowTierName,
    localShowProgramName,
  } = useTierState(tier, showTierName, showProgramName);

  // Redux state (optimized selectors)
  const creatingContract = useAppSelector(selectCreatingContract);
  const isTierAddingToCart = useAppSelector(selectIsTierAddingToCart(tier?.id));

  // Memoized computed values (avoid recalculation)
  const tierIsCurrent = useMemo(() => isTierCurrent(tier), [tier]);
  const tierIsSoldOut = useMemo(() => isTierSoldOut(tier), [tier]);
  const contractIsFreeTier = useMemo(
    () => isContractFreeTier(membership.contract),
    [membership.contract]
  );

  // Memoized helpers (computed once per prop change)
  const isTermSelected = useMemo(() => !!termId || tier?.terms?.length <= 1, [termId, tier?.terms]);

  const isFreeAndOneTier = useMemo(
    () => tier?.freeTier && membership?.tiers?.length === 1,
    [tier?.freeTier, membership?.tiers]
  );

  const isOneTierAndNoName = useMemo(
    () => membership?.tiers?.length === 1 && !tier?.title,
    [membership?.tiers, tier?.title]
  );

  const returnToUrl = useMemo(() => {
    if (redirectToCurrentUrl) {
      return getCurrentUrl();
    }
    if (redirectUrl) return redirectUrl;
    if (tier.postPurchaseUrl) return tier.postPurchaseUrl;
    if (accountSettings?.defaultLandingUrl) return accountSettings.defaultLandingUrl;
    return `${window.location.origin}/apps/single/membership`;
  }, [redirectToCurrentUrl, redirectUrl, tier.postPurchaseUrl, accountSettings?.defaultLandingUrl]);

  // Memoized event handlers (prevent child re-renders)
  const handleActionClick = useCallback(
    (interactionType: InteractionType) => {
      const confirmChangeModalProps: MembershipConfirmChangeModalProps = {
        membership,
        tier,
        termId,
        interactionType,
        onClose: (selectedTermId: string | null) => {
          if (selectedTermId) {
            const changeType = getChangeType(tier.contractState);
            const immediate = changeType === 'UPGRADE' || membership.selectedTier?.freeTier;
            const request = {
              contractId: membership.contractId,
              fanSubscriptionId: membership.id,
              tierId: tier.id,
              termId: selectedTermId,
              changeType,
              immediate,
            } as MembershipContractUpdateRequest;
            dispatch(updateMembership(request));
          }
          modalService.closeAll();
        },
      };
      modalService.open({
        component: MembershipConfirmChangeModal,
        props: confirmChangeModalProps,
      });
    },
    [tier, membership, termId, dispatch]
  );

  const handleManage = useCallback(() => {
    const manageModalProps: MembershipManageModalProps = {
      membership,
      tier,
    };
    modalService.open({
      component: MembershipManageModal,
      props: manageModalProps,
    });
  }, [membership, tier]);

  const handleCancelPendingAction = useCallback(() => {
    const membershipLabel = getMembershipLabel(membership);
    let changeType = 'Term Change';
    switch (membership.latestPendingChange) {
      case PendingChangeState.CANCEL:
        changeType = `${membershipLabel} Cancellation`;
        break;
      case PendingChangeState.DOWNGRADE:
        changeType = 'Downgrade';
        break;
      case PendingChangeState.UPGRADE:
        changeType = 'Upgrade';
        break;
    }
    const confirmCancelPendingActionModalProps: MembershipConfirmCancelPendingActionModalProps = {
      title: 'Cancel ' + changeType,
      message: `Your ${membershipLabel.toLowerCase()} will revert back to its previous settings.`,
      confirmText: 'Yes, Cancel ' + changeType,
      onConfirm: () => {
        dispatch(
          cancelMembershipUpdate({
            contractId: membership.contractId,
            fanSubscriptionId: membership.id,
          })
        );
        modalService.closeAll();
      },
      onCancel: () => {
        modalService.closeAll();
      },
    };
    modalService.open({
      component: MembershipConfirmCancelPendingActionModal,
      props: confirmCancelPendingActionModalProps,
    });
  }, [membership, dispatch]);

  const handleJoin = useCallback(() => {
    // Prevent duplicate contract creation
    if (creatingContract || isTierAddingToCart) {
      return;
    }

    MembershipJoinFlowService.initiateMembershipJoin(
      {
        tier,
        termId: termId,
        redirectUrl: returnToUrl,
      },
      dispatch,
      openAuthModal
    );
  }, [tier, termId, returnToUrl, dispatch, openAuthModal, creatingContract, isTierAddingToCart]);

  const handleTermSelectionChange = useCallback(
    (e: Event) => {
      setTermId((e.target as HTMLSelectElement).value);
    },
    [setTermId]
  );

  const loadPerks = useCallback(() => {
    window.location.href = tier.postPurchaseUrl || '/apps/single/membership';
  }, [tier.postPurchaseUrl]);

  // Render buttons based on contract state
  // Sonarqube gives this function a Cyclomatic Complexity score of 28. Default threshold is 15.
  // This function should be refactored to reduce the score.
  const renderActionButtons = () => {
    if (tierIsCurrent || !membership.pendingChanges) {
      if (
        tier.contractState === ContractState.ACTIVE ||
        tier.contractState === ContractState.PAYMENT_FAILED
      ) {
        return (
          <>
            {showViewPerks && (
              <button class="btn button" onClick={loadPerks}>
                {t('membership.viewMyPerks')}
              </button>
            )}
            {!tier.freeTier && (
              <button class="btn button" onClick={handleManage}>
                {t('membership.manage')}
              </button>
            )}
          </>
        );
      } else if (tier.contractState === ContractState.UPGRADE) {
        if (tierIsSoldOut) {
          return (
            <button class="btn button" disabled>
              {t('product.soldOut')}
            </button>
          );
        }
        let upgradeButtonText = t('membership.upgrade');
        if (creatingContract) {
          upgradeButtonText = t('membership.joining');
        } else if (isTierAddingToCart) {
          upgradeButtonText = t('membership.addingToCart');
        }
        return (
          <button
            class="btn button"
            disabled={!isTermSelected || isTierAddingToCart || creatingContract}
            onClick={() =>
              contractIsFreeTier ? handleJoin() : handleActionClick(InteractionType.UPGRADE)
            }
          >
            {upgradeButtonText}
          </button>
        );
      } else if (tier.contractState === ContractState.DOWNGRADE) {
        if (tierIsSoldOut) {
          return (
            <button class="btn button" disabled>
              {t('product.soldOut')}
            </button>
          );
        }
        return (
          <button
            class="btn button"
            disabled={!isTermSelected}
            onClick={() => handleActionClick(InteractionType.DOWNGRADE)}
          >
            {t('membership.downgrade')}
          </button>
        );
      } else if (tier.contractState === ContractState.PENDING) {
        return (
          <div class="pending-buttons">
            <button class="btn button" onClick={handleCancelPendingAction}>
              {t('membership.cancelPendingChanges')}
            </button>
          </div>
        );
      } else {
        if (showQuickAdd) {
          if (tierIsSoldOut) {
            return (
              <button class="btn button" disabled>
                {t('product.soldOut')}
              </button>
            );
          }

          const isLoading = creatingContract || isTierAddingToCart;

          const buttonText = creatingContract
            ? t('membership.joining')
            : isTierAddingToCart
              ? t('membership.addingToCart')
              : t('membership.join');

          return (
            <button
              class="btn button"
              disabled={!isTermSelected || isLoading || creatingContract || isTierAddingToCart}
              onClick={handleJoin}
            >
              {buttonText}
            </button>
          );
        }
        return null;
      }
    }
    return null;
  };

  return (
    <div
      class={`single-membership-tier${layout === 'row' ? ' single-membership-tier--row' : ''}`}
    >
      <div class="single-card-option-wrapper">
        {showTierImage && tier.imageUrl && (
          <div class="single-card-image" style={{ backgroundImage: `url(${tier.imageUrl})` }}>
            {tierIsCurrent && <span class="status-badge active">{t('membership.active')}</span>}
          </div>
        )}
        <div class="single-card-option-inner">
          {!isOneTierAndNoName && (
            <h3 class="tier-title text-alignment">
              {titleFormat === 'bullet' && (
                <>
                  {localShowProgramName && <span>{membership.title}</span>}
                  {localShowProgramName && localShowTierName && tier.title && (
                    <span style={{ margin: '0 .25em' }}>•</span>
                  )}
                </>
              )}
              {localShowTierName && tier.title && <span>{tier.title}</span>}
              {localShowProgramName && titleFormat === 'small' && (
                <small class="membership-title">{membership.title}</small>
              )}
            </h3>
          )}
          {isOneTierAndNoName && localShowProgramName && (
            <h3 class="tier-title text-alignment">
              <span>{membership.title}</span>
            </h3>
          )}
          {!tierIsCurrent && (
            <>
              {showTierDescription && tier.aboutMessage && (
                <div
                  class="tier-description text-alignment"
                  dangerouslySetInnerHTML={{ __html: tier.aboutMessage }}
                />
              )}
            </>
          )}
          {tierIsCurrent && (
            <div class="active-info">
              <div class="active-row">
                <span>{t('membership.nextBillingDate')}</span>
                <span>{formatDate(membership.nextBillingDate)}</span>
              </div>
              <div class="active-row">
                <span>
                  {t('membership.term')}
                  {tier.terms.length > 1 && !membership.pendingChanges && (
                    <span class="change-term" onClick={handleManage}>
                      &nbsp;{t('membership.changeTerm')}
                    </span>
                  )}
                </span>
                <span>
                  {isNonRenewingTerm(membership.selectedTerm)
                    ? t('membership.oneTimePurchase')
                    : getMembershipTermName(membership.selectedTerm)}
                </span>
              </div>
              <div class="active-row">
                <span>{t('membership.price')}</span>
                <span>
                  {tier.freeTier
                    ? t('membership.free')
                    : shopifyCurrency(membership.selectedTerm.convertedPrice, false)}
                </span>
              </div>
            </div>
          )}
          <div class="single-card-option-footer">
            {!tierIsCurrent && !isFreeAndOneTier && showPrice && tier.terms?.length === 1 && (
              <div class="tier-price text-alignment">
                {tier.freeTier ? (
                  t('membership.free')
                ) : (
                  <>
                    {shopifyCurrency(tier.terms[0].convertedPrice ?? tier.terms[0].price, false)}
                    {!isNonRenewingTerm(tier.terms[0]) && (
                      <>
                        &nbsp;{t('membership.per')} {getMembershipTermName(tier.terms[0], false)}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
            {(!tierIsCurrent || (showQuickAdd && !tier.contractState)) &&
              tier.terms?.length > 1 &&
              !membership.pendingChanges && (
                <div class="select-wrap">
                  <select
                    value={termId}
                    onChange={(e) => handleTermSelectionChange(e)}
                    class="select text-alignment"
                  >
                    {tier.terms.map((term) =>
                      term.id !== membership.selectedTerm?.id ? (
                        <option key={term.id} value={term.id}>
                          {shopifyCurrency(term.convertedPrice, false)}
                          {!isNonRenewingTerm(term) && (
                            <>
                              {' '}
                              {t('membership.per')} {getMembershipTermName(term, false)}
                            </>
                          )}
                        </option>
                      ) : null
                    )}
                  </select>
                  <IconCaret />
                </div>
              )}
            {renderActionButtons()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Memoize the entire component to prevent unnecessary re-renders
// Only re-render if tier.id or membership.id changes
export const MembershipTier = memo(MembershipTierComponent);
